/**
 * free-shipping-bar.js
 * Drop into assets/
 *
 * Listens for Shopify cart changes (AJAX add-to-cart, quantity updates,
 * removals) and refreshes the progress bar without a full page reload.
 *
 * Works with:
 *  • Dawn and Dawn-derived themes  (cart:refresh, cart:updated custom events)
 *  • Any theme that dispatches a native "cart:updated" or "cart:change" event
 *  • Themes that use Shopify's Section Rendering API
 *  • Fallback: polling the /cart.js endpoint
 */

(function () {
  'use strict';

  // ── Threshold map (mirrors the Liquid snippet) ──────────────────────────
  // Unit: smallest currency unit (cents / øre / etc.)
  // Edit here AND in the Liquid snippet to keep them in sync.
  const THRESHOLDS = {
    DK: 49900, SE: 49900, NO: 59900, FI: 4990,
    DE: 4990,  NL: 4990,  BE: 4990,  FR: 5990,
    AT: 5990,  CH: 5990,  IT: 6990,  ES: 6990,
    PT: 6990,  PL: 19900, GB: 3990,  IE: 4990,
    US: 4900,  CA: 5900,  AU: 7900,  NZ: 8900,
    '*': 4990
  };

  // ── i18n strings (add more locales as needed) ────────────────────────────
  // Keys mirror locales/*.json; these are only used when re-rendering via JS.
  const I18N = {
    en: {
      spend_more: 'Spend <strong>{amount}</strong> more for free shipping',
      eligible:   '🎉 You\'ve unlocked free shipping!',
      aria_label: 'Free shipping progress'
    },
    da: {
      spend_more: 'Køb for <strong>{amount}</strong> mere og få gratis fragt',
      eligible:   '🎉 Du har optjent gratis fragt!',
      aria_label: 'Fremskridt mod gratis fragt'
    },
    sv: {
      spend_more: 'Handla för <strong>{amount}</strong> mer och få fri frakt',
      eligible:   '🎉 Du har fått fri frakt!',
      aria_label: 'Framsteg mot fri frakt'
    },
    nb: {
      spend_more: 'Kjøp for <strong>{amount}</strong> mer og få gratis frakt',
      eligible:   '🎉 Du har fått gratis frakt!',
      aria_label: 'Fremgang mot gratis frakt'
    },
    fi: {
      spend_more: 'Lisää ostoksia <strong>{amount}</strong> verran ilmaisen toimituksen saamiseksi',
      eligible:   '🎉 Olet ansainnut ilmaisen toimituksen!',
      aria_label: 'Edistyminen ilmaista toimitusta kohti'
    },
    de: {
      spend_more: 'Noch <strong>{amount}</strong> bis zum kostenlosen Versand',
      eligible:   '🎉 Du hast kostenlosen Versand freigeschaltet!',
      aria_label: 'Fortschritt zum kostenlosen Versand'
    },
    fr: {
      spend_more: 'Plus que <strong>{amount}</strong> pour la livraison gratuite',
      eligible:   '🎉 Vous avez débloqué la livraison gratuite !',
      aria_label: 'Progression vers la livraison gratuite'
    },
    nl: {
      spend_more: 'Nog <strong>{amount}</strong> voor gratis verzending',
      eligible:   '🎉 Je hebt gratis verzending ontgrendeld!',
      aria_label: 'Voortgang naar gratis verzending'
    },
    it: {
      spend_more: 'Ancora <strong>{amount}</strong> per la spedizione gratuita',
      eligible:   '🎉 Hai sbloccato la spedizione gratuita!',
      aria_label: 'Progresso verso la spedizione gratuita'
    },
    es: {
      spend_more: 'Añade <strong>{amount}</strong> más para envío gratuito',
      eligible:   '🎉 ¡Has desbloqueado el envío gratuito!',
      aria_label: 'Progreso hacia el envío gratuito'
    },
    pl: {
      spend_more: 'Dodaj jeszcze <strong>{amount}</strong>, aby uzyskać darmową dostawę',
      eligible:   '🎉 Odblokowałeś darmową dostawę!',
      aria_label: 'Postęp w kierunku darmowej dostawy'
    },
    pt: {
      spend_more: 'Faltam <strong>{amount}</strong> para envio gratuito',
      eligible:   '🎉 Você desbloqueou o envio gratuito!',
      aria_label: 'Progresso para envio gratuito'
    },
  };

  // ── Helpers ──────────────────────────────────────────────────────────────

  /** Detect the active Shopify storefront locale (2-char code). */
  function detectLocale() {
    const meta = document.querySelector('meta[name="shopify-locale"]');
    if (meta) return meta.content.slice(0, 2);
    return (document.documentElement.lang || 'en').slice(0, 2);
  }

  /** Get strings for locale, falling back to English. */
  function getStrings(locale) {
    return I18N[locale] || I18N['en'];
  }

  /**
   * Format a minor-unit price using the Intl API.
   * Shopify stores the currency code in Shopify.currency.active when the
   * Shopify object is available; otherwise we fall back to navigator locale.
   */
  function formatMoney(cents) {
    const currencyCode =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'USD';
    const locale =
      (window.Shopify && window.Shopify.locale) ||
      navigator.language || 'en';

    // Determine decimal places (JPY, KRW etc. have 0)
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
    });
    const parts = formatter.formatToParts(1);
    const hasFraction = parts.some(p => p.type === 'fraction');
    const divisor = hasFraction ? 100 : 1;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0,
    }).format(cents / divisor);
  }

  /** Resolve the threshold for the given country code. */
  function getThreshold(countryCode) {
    return THRESHOLDS[countryCode] || THRESHOLDS['*'];
  }

  // ── DOM update ───────────────────────────────────────────────────────────

  function updateBar(cartTotal) {
    const bar = document.querySelector('free-shipping-bar');
    if (!bar) return;

    const country   = bar.dataset.country || '*';
    const threshold = getThreshold(country);
    const locale    = detectLocale();
    const strings   = getStrings(locale);
    const remaining = Math.max(0, threshold - cartTotal);
    const pct       = Math.min(100, (cartTotal / threshold) * 100);
    const eligible  = cartTotal >= threshold;

    // ── Update wrapper state ──
    bar.dataset.progress = pct.toFixed(1);
    bar.dataset.eligible = eligible;
    bar.classList.toggle('fsb-wrapper--eligible', eligible);

    // ── Update message ──
    const msgEl = bar.querySelector('.fsb-message');
    if (msgEl) {
      if (eligible) {
        msgEl.innerHTML = `<span class="fsb-message__eligible">${strings.eligible}</span>`;
      } else {
        const amountHtml = formatMoney(remaining);
        msgEl.innerHTML = `<span class="fsb-message__progress">${
          strings.spend_more.replace('{amount}', amountHtml)
        }</span>`;
      }
    }

    // ── Update progress fill ──
    const fill = bar.querySelector('.fsb-fill');
    if (fill) fill.style.setProperty('--fsb-pct', pct.toFixed(1) + '%');

    // ── Update ARIA ──
    const track = bar.querySelector('.fsb-track');
    if (track) track.setAttribute('aria-valuenow', Math.round(pct));

    // ── Animate truck on unlock ──
    const truck = bar.querySelector('.fsb-icon--truck');
    if (truck && eligible) {
      truck.classList.add('fsb-icon--truck-active');
    } else if (truck) {
      truck.classList.remove('fsb-icon--truck-active');
    }
  }

  // ── Fetch latest cart total from Shopify ─────────────────────────────────

  async function fetchCartAndUpdate() {
    try {
      const res  = await fetch('/cart.js', { cache: 'no-store' });
      const data = await res.json();
      updateBar(data.total_price);   // total_price is already in cents
    } catch (e) {
      // Silently fail — server-rendered state is still correct
      console.warn('[free-shipping-bar] Could not fetch cart:', e);
    }
  }

  // ── Event listeners ──────────────────────────────────────────────────────

  // Dawn / Debut / Broadcast custom events
  const CART_EVENTS = [
    'cart:refresh',
    'cart:updated',
    'cart:change',
    'cart-update',
    // Broadcast (older Dawn themes)
    'on:cart:update',
  ];

  CART_EVENTS.forEach(evt => {
    document.addEventListener(evt, fetchCartAndUpdate);
  });

  // Intercept fetch/XHR calls to /cart/ endpoints (add, change, update)
  // so we update even when themes don't fire custom events.
  (function patchFetch() {
    const _fetch = window.fetch;
    window.fetch = function (...args) {
      const url = (args[0] && (args[0].url || args[0])) || '';
      const result = _fetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        result.then(res => res.clone().json())
               .then(data => {
                 if (data && typeof data.total_price === 'number') {
                   updateBar(data.total_price);
                 } else {
                   // /cart/add.js returns line item, not cart — re-fetch
                   fetchCartAndUpdate();
                 }
               })
               .catch(() => fetchCartAndUpdate());
      }
      return result;
    };
  })();

  // ── Init ─────────────────────────────────────────────────────────────────

  // On DOMContentLoaded, sync with the live cart (handles page cache scenarios)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCartAndUpdate);
  } else {
    fetchCartAndUpdate();
  }

})();
